import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSnackbar } from '../contexts/SnackbarContext';
import { api } from '../lib/api';
import { Badge, Btn, Modal } from '../components';
import { InstructorPreviewModal } from '../components/InstructorPreviewModal';
import { AdminBookingsPanel } from '../components/AdminBookingsPanel';
import { AdminBookingPaymentsPanel } from '../components/AdminBookingPaymentsPanel';
import { AdminApplicationsPanel, APPLICATION_STATUS, instructorFormFromApplication } from '../components/AdminApplicationsPanel';
import { ReportedConversationsPanel } from '../components/messages/ReportedConversationsPanel';
import { DashboardMenu, DashboardLogoutButton } from '../components/DashboardMenu';
import { DASHBOARD_TABS, tabLabel } from '../lib/dashboardTabs';
import { formatInTz, DEFAULT_TIMEZONE } from '../lib/timezone';
import { peso } from '../lib/format';
import { palette as T, type } from '../design';

// Admin views one explicit, labeled timezone (the app default) rather than silently
// browser-local, so booking/audit times read the same for every admin (TTT-14).
const ADMIN_TZ = DEFAULT_TIMEZONE;
// Signup/publish dates: date only, but still pinned to ADMIN_TZ so a row doesn't read as a
// different day depending on where the admin happens to be sitting.
const DATE_ONLY = { month: 'short', day: 'numeric', year: 'numeric' };
// The answer route's reply when someone already answered the application.
const CONFLICT_STATUS = 409;
// The reset-password and delete-user pop-ups are named by their headings (DS-2.3). Only one of
// each is ever open, so fixed ids are safe.
const RESET_TITLE_ID = 'admin-reset-password-title';
const DELETE_TITLE_ID = 'admin-delete-user-title';


var inputStyle = { padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' };

// Server Health formatting. The API returns raw bytes/seconds on purpose — presentation is the
// frontend's job, and the raw numbers stay useful for anything else that reads the endpoint.
function formatBytes(bytes) {
  if (typeof bytes !== 'number' || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return (value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)) + ' ' + units[unit];
}

function formatUptime(seconds) {
  if (typeof seconds !== 'number' || seconds < 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return days + 'd ' + hours + 'h';
  if (hours > 0) return hours + 'h ' + mins + 'm';
  return mins + 'm';
}

// Amber past 75%, red past 90% — a nearly-full disk should be obvious without reading the number.
function UsageBar({ pct, showLabel }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const colour = clamped >= 90 ? T.errorRed : clamped >= 75 ? T.terracotta : T.sageGreen;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ height: 6, borderRadius: 3, background: T.warmGrey, overflow: 'hidden' }}>
        <div style={{ width: clamped + '%', height: '100%', background: colour, transition: 'width 0.3s' }} />
      </div>
      {showLabel && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>{clamped}% used</p>}
    </div>
  );
}

function AdminDashboard({ setPage }) {
  const isMobile = useIsMobile();
  const { showSnackbar } = useSnackbar();
  const [tab, setTab] = React.useState('overview');
  const [stats, setStats] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [instructors, setInstructors] = React.useState([]);
  const [bookings, setBookings] = React.useState([]);
  const [blogPosts, setBlogPosts] = React.useState([]);
  const [adminActions, setAdminActions] = React.useState([]);
  const [appErrors, setAppErrors] = React.useState([]);
  const [errorTypes, setErrorTypes] = React.useState([]);
  const [errorFilters, setErrorFilters] = React.useState({ type: '', since: '' });
  const [health, setHealth] = React.useState(null);
  const [healthError, setHealthError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState({});
  const [blogForm, setBlogForm] = React.useState({ title: '', excerpt: '', content: '', category: 'Learning' });
  const [blogSaving, setBlogSaving] = React.useState(false);
  const [resetModalUser, setResetModalUser] = React.useState(null);
  const [resetResult, setResetResult] = React.useState(null);
  const [resetLoading, setResetLoading] = React.useState(false);
  const [deleteModalUser, setDeleteModalUser] = React.useState(null);
  const [deletePassword, setDeletePassword] = React.useState('');
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [createAdminForm, setCreateAdminForm] = React.useState({ email: '', name: '', password: '' });
  const [createAdminLoading, setCreateAdminLoading] = React.useState(false);
  const [createAdminOpen, setCreateAdminOpen] = React.useState(false);
  const EMPTY_INSTRUCTOR = { name: '', displayName: '', email: '', password: '', language: 'JP', flag: '🇯🇵', country: '', teaches: '', native: '', bio: '', teachingStyle: '' };
  const [createInstructorForm, setCreateInstructorForm] = React.useState(EMPTY_INSTRUCTOR);
  const [createInstructorLoading, setCreateInstructorLoading] = React.useState(false);
  const [createInstructorOpen, setCreateInstructorOpen] = React.useState(false);
  // Instructor id whose public profile preview is open (#306), or null.
  const [previewInstructorId, setPreviewInstructorId] = React.useState(null);
  // The application the create-instructor form was opened from by Invite (AD-4.6), or null.
  // It is marked invited only after the form saves.
  const [invitingApplication, setInvitingApplication] = React.useState(null);
  // Bumped after an invite saves, so the Applications list reloads and reads invited.
  const [applicationsKey, setApplicationsKey] = React.useState(0);
  const createInstructorRef = React.useRef(null);
  const fullNameRef = React.useRef(null);

  const fetchAll = async () => {
    try {
      const [s, u, i, b, p, a] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/instructors'),
        // Recent bookings on the overview: page 1 of the list, newest session first.
        api.get('/admin/bookings?page=1'),
        api.get('/blog-posts'),
        api.get('/admin/actions'),
      ]);
      setStats(s);
      setUsers(u.users || []);
      setInstructors(i.instructors || []);
      setBookings(b.bookings || []);
      setBlogPosts(p.posts || []);
      setAdminActions(a.actions || []);
    } catch (err) {
      console.error('Admin fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAll();
  }, []);

  // Error log is fetched on its own because it takes filter params — refetching everything on
  // every filter change would be wasteful.
  const fetchErrors = React.useCallback(async (filters) => {
    const params = new URLSearchParams();
    if (filters.type) params.set('type', filters.type);
    if (filters.since) params.set('since', filters.since);
    const qs = params.toString();
    try {
      const res = await api.get('/admin/errors' + (qs ? '?' + qs : ''));
      setAppErrors(res.errors || []);
      setErrorTypes(res.types || []);
    } catch (err) {
      console.error('Error log fetch failed:', err);
    }
  }, []);

  React.useEffect(() => {
    if (tab !== 'errors') return;
    fetchErrors(errorFilters);
  }, [tab, errorFilters, fetchErrors]);

  // Health polls only while its tab is open — the other tabs shouldn't pay for it, and there's no
  // stored history to backfill, so there's nothing to gain from polling in the background.
  React.useEffect(() => {
    if (tab !== 'system') return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await api.get('/admin/system');
        if (!cancelled) { setHealth(res); setHealthError(null); }
      } catch (err) {
        if (!cancelled) setHealthError(err?.data?.message || 'Could not read server health.');
      }
    };

    poll();
    const id = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tab]);

  const handleVerify = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.patch(`/admin/instructors/${id}/verify`);
      await fetchAll();
      showSnackbar('Instructor verified and listed in the marketplace.', { variant: 'success' });
    } catch (err) {
      showSnackbar('We could not verify this instructor: ' + (err.data?.message || err.message) + ' Try again.', { variant: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleReject = async (id) => {
    if (!confirm('Reject this instructor? They will be hidden from students.')) return;
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.patch(`/admin/instructors/${id}/reject`);
      await fetchAll();
      showSnackbar('Instructor rejected.', { variant: 'success' });
    } catch (err) {
      showSnackbar('Failed to reject: ' + (err.data?.error || err.message), { variant: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleSuspend = async (id) => {
    if (!confirm('Suspend this user? They will not be able to log in.')) return;
    setActionLoading(prev => ({ ...prev, [`user-${id}`]: true }));
    try {
      await api.patch(`/admin/users/${id}/suspend`);
      await fetchAll();
      showSnackbar('User suspended successfully.', { variant: 'success' });
    } catch (err) {
      showSnackbar('Failed to suspend: ' + (err.data?.error || err.message), { variant: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [`user-${id}`]: false }));
    }
  };

  const handleResetPassword = async (id) => {
    setResetLoading(true);
    try {
      const data = await api.post(`/admin/users/${id}/reset-password`);
      setResetResult(data);
      showSnackbar('Temporary password generated.', { variant: 'success' });
    } catch (err) {
      showSnackbar('Failed to reset password: ' + (err.data?.error || err.message), { variant: 'error' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/users/${deleteModalUser.id}`, { password: deletePassword });
      setDeleteModalUser(null);
      setDeletePassword('');
      await fetchAll();
      showSnackbar('User account permanently deleted.', { variant: 'success' });
    } catch (err) {
      showSnackbar('Failed to delete: ' + (err.data?.message || err.message), { variant: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreateAdminLoading(true);
    try {
      await api.post('/admin/users', createAdminForm);
      setCreateAdminForm({ email: '', name: '', password: '' });
      setCreateAdminOpen(false);
      await fetchAll();
      showSnackbar('Admin user created successfully.', { variant: 'success' });
    } catch (err) {
      showSnackbar('Failed to create admin: ' + (err.data?.error || err.message), { variant: 'error' });
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleCreateInstructor = async (e) => {
    e.preventDefault();
    setCreateInstructorLoading(true);
    try {
      const f = createInstructorForm;
      const langLabel = f.language === 'JP' ? 'Japanese' : f.language === 'ZH' ? 'Chinese' : f.language;
      const toArray = (s, fallback) => {
        const arr = (s || '').split(',').map(x => x.trim()).filter(Boolean);
        return arr.length ? arr : fallback;
      };
      await api.post('/admin/instructors', {
        email: f.email,
        password: f.password,
        name: f.name,
        displayName: f.displayName,
        flag: f.flag,
        country: f.country,
        language: f.language,
        teaches: toArray(f.teaches, [langLabel]),
        native: toArray(f.native, [langLabel]),
        bio: f.bio,
        teachingStyle: f.teachingStyle,
      });
    } catch (err) {
      showSnackbar('Failed to create instructor: ' + (err.data?.message || err.data?.error || err.message), { variant: 'error' });
      setCreateInstructorLoading(false);
      return;
    }
    const application = invitingApplication;
    setCreateInstructorForm(EMPTY_INSTRUCTOR);
    setCreateInstructorOpen(false);
    setInvitingApplication(null);
    if (application) await markInvited(application);
    else showSnackbar('Instructor created successfully.', { variant: 'success' });
    await fetchAll();
    setCreateInstructorLoading(false);
  };

  // The account is saved; now the application reads invited. A failure here leaves the account
  // in place, so admin is told the application did not change rather than that nothing worked.
  const markInvited = async (application) => {
    try {
      await api.patch(`/admin/applications/${application.id}`, { status: APPLICATION_STATUS.INVITED });
      showSnackbar(`Instructor created. ${application.name}'s application reads invited.`, { variant: 'success' });
    } catch (err) {
      console.error('Marking the application invited failed:', err);
      // A 409 means someone answered it first: it is not new, and the list reloads below.
      showSnackbar(err?.status === CONFLICT_STATUS
        ? 'The instructor account is saved. This application was already answered, so it was not marked invited.'
        : 'The instructor account is saved, but the application still reads new: '
          + (err.data?.message || 'we could not reach the server.') + ' Refresh Applications to check it.', { variant: 'error' });
    } finally {
      setApplicationsKey((k) => k + 1);
    }
  };

  // Invite (AD-4.6): open the create-instructor form filled in from the application. Nothing is
  // saved until admin finishes the form.
  const handleInviteApplication = (application) => {
    setInvitingApplication(application);
    setCreateInstructorForm({ ...EMPTY_INSTRUCTOR, ...instructorFormFromApplication(application) });
    setCreateInstructorOpen(true);
  };

  const closeCreateInstructor = (e) => {
    e?.preventDefault?.(); // The Cancel button is type="button"; this stays as a second guard.
    setCreateInstructorOpen(false);
    if (invitingApplication) {
      setInvitingApplication(null);
      setCreateInstructorForm(EMPTY_INSTRUCTOR);
    }
  };

  // Create Instructor from the Instructors tab starts empty, never from an earlier invite.
  const openCreateInstructor = () => {
    if (invitingApplication) {
      setInvitingApplication(null);
      setCreateInstructorForm(EMPTY_INSTRUCTOR);
    }
    setCreateInstructorOpen(true);
  };

  // On a phone the form opens above the list, out of sight of the Invite button tapped below.
  // Keyboard focus follows to the first field, so it does not stay on the row under the form.
  React.useEffect(() => {
    if (!invitingApplication) return;
    createInstructorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    fullNameRef.current?.focus({ preventScroll: true });
  }, [invitingApplication]);

  if (loading) {
    return (
      <div style={{ paddingTop: 0, display: 'flex', minHeight: '100vh', background: T.ivory, alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading admin dashboard…</p>
      </div>
    );
  }

  const tabs = DASHBOARD_TABS.admin;

  // One create-instructor form, shown on the Instructors tab and, after Invite, on Applications.
  const createInstructorCard = (
    <div ref={createInstructorRef} style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24, marginBottom: 20, scrollMarginTop: 16 }}>
      <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 16px' }}>Create Instructor</h3>
      {invitingApplication && (
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '-8px 0 16px', lineHeight: 1.5 }}>
          Filled in from {invitingApplication.name}&apos;s application. Choose a password and check the details. When you save, the application reads invited.
        </p>
      )}
      <form onSubmit={handleCreateInstructor} aria-label="Create Instructor" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
          <input ref={fullNameRef} value={createInstructorForm.name} onChange={e => setCreateInstructorForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" aria-label="Full name" required style={inputStyle} />
          <input value={createInstructorForm.displayName} onChange={e => setCreateInstructorForm(p => ({ ...p, displayName: e.target.value }))} placeholder="Display name (e.g. Mari-sensei)" aria-label="Display name" required style={inputStyle} />
          <input type="email" value={createInstructorForm.email} onChange={e => setCreateInstructorForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" aria-label="Email" required style={inputStyle} />
          <input type="password" value={createInstructorForm.password} onChange={e => setCreateInstructorForm(p => ({ ...p, password: e.target.value }))} placeholder="Password (min 8 chars)" aria-label="Password" required minLength={8} style={inputStyle} />
          <select value={createInstructorForm.language} onChange={e => setCreateInstructorForm(p => ({ ...p, language: e.target.value }))} aria-label="Language" style={inputStyle}>
            <option value="JP">Japanese</option>
            <option value="ZH">Chinese</option>
          </select>
          <input value={createInstructorForm.flag} onChange={e => setCreateInstructorForm(p => ({ ...p, flag: e.target.value }))} placeholder="Flag emoji (e.g. 🇯🇵)" aria-label="Flag" required style={inputStyle} />
          <input value={createInstructorForm.country} onChange={e => setCreateInstructorForm(p => ({ ...p, country: e.target.value }))} placeholder="Country (e.g. Japan (Osaka))" aria-label="Country" required style={inputStyle} />
          <input value={createInstructorForm.teaches} onChange={e => setCreateInstructorForm(p => ({ ...p, teaches: e.target.value }))} placeholder="Teaches (comma-separated, defaults to language)" aria-label="Teaches" style={inputStyle} />
          <input value={createInstructorForm.native} onChange={e => setCreateInstructorForm(p => ({ ...p, native: e.target.value }))} placeholder="Native languages (comma-separated)" aria-label="Native languages" style={inputStyle} />
        </div>
        <textarea value={createInstructorForm.bio} onChange={e => setCreateInstructorForm(p => ({ ...p, bio: e.target.value }))} placeholder="Bio" aria-label="Bio" rows={3} required style={{ ...inputStyle, resize: 'vertical' }} />
        <textarea value={createInstructorForm.teachingStyle} onChange={e => setCreateInstructorForm(p => ({ ...p, teachingStyle: e.target.value }))} placeholder="Teaching style" aria-label="Teaching style" rows={2} required style={{ ...inputStyle, resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn type="submit" disabled={createInstructorLoading}>{createInstructorLoading ? 'Creating…' : 'Create Instructor'}</Btn>
          <Btn type="button" size="sm" variant="ghost" onClick={closeCreateInstructor}>Cancel</Btn>
        </div>
      </form>
    </div>
  );

  return (
    <div style={{ paddingTop: 0, display: 'flex', minHeight: '100vh', background: T.ivory, flexDirection: isMobile ? 'column' : 'row' }}>
      {/* Sidebar */}
      {!isMobile && (
        <div style={{ width: 220, flexShrink: 0, background: T.white, borderRight: '1px solid ' + T.border, minHeight: 'calc(100vh - 64px)', padding: '24px 0' }}>
          <div style={{ padding: '0 16px 20px', borderBottom: '1px solid ' + T.border, marginBottom: 12 }}>
            <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h4, color: T.espresso, margin: 0 }}>Admin</p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '4px 0 0' }}>Ropida Admin Panel</p>
          </div>
          {tabs.map(item => (
            <button key={item.id} onClick={() => setTab(item.id)}
              style={{
                width: '100%', padding: '11px 20px', border: 'none',
                background: tab === item.id ? T.ivory : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                textAlign: 'left', borderRight: tab === item.id ? '3px solid ' + T.terracotta : '3px solid transparent',
                transition: 'all 0.15s'
              }}>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: tab === item.id ? 600 : 400, fontSize: type.body, color: tab === item.id ? T.terracotta : T.walnut }}>{item.label}</span>
            </button>
          ))}
          <div style={{ padding: '20px 16px 0', borderTop: '1px solid ' + T.border, marginTop: 12 }}>
            <button onClick={() => setPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>← Back to site</button>
            <DashboardLogoutButton setPage={setPage} />
          </div>
        </div>
      )}

      {/* Phone menu: every admin tab, Back to site and Log out */}
      <DashboardMenu tabs={tabs} active={tab} onSelect={setTab} setPage={setPage} />

      <div style={{ flex: 1, padding: isMobile ? '16px' : '32px', overflowY: 'auto', maxHeight: isMobile ? 'none' : 'calc(100vh - 64px)', paddingBottom: '32px' }}>
        <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 24px' }}>{tabLabel(tabs, tab)}</h1>

        {/* ── Overview ── */}
        {tab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 16, marginBottom: 24 }}>
              {[
                { label: 'Total users', value: stats?.totalUsers ?? 0, color: T.ivory },
                { label: 'Instructors', value: stats?.totalInstructors ?? 0, color: T.warmGrey },
                { label: 'Bookings', value: stats?.totalBookings ?? 0, color: T.ivory },
                // Confirmed and completed bookings only. Students pay instructors directly, so this
                // is booking value, not platform revenue.
                { label: 'Booking value', value: peso(stats?.bookingValue), note: 'Paid by students to instructors', color: T.warmGrey },
              ].map(s => (
                <div key={s.label} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '20px 18px' }}>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' }}>{s.label}</p>
                  <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: 0 }}>{s.value}</p>
                  {s.note && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: '6px 0 0' }}>{s.note}</p>}
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 }}>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 16px' }}>Recent bookings</h3>
                {bookings.slice(0, 5).map(b => (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid ' + T.border }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{b.session?.title || b.sessionType} · {b.instructorName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: 0 }}>{formatInTz(b.scheduledAt, ADMIN_TZ, { month: 'short', day: 'numeric', year: 'numeric', timeZoneName: 'short' })} · {b.studentName}</p>
                    </div>
                    <Badge variant={b.status === 'confirmed' ? 'sage' : b.status === 'pending' ? 'gold' : 'subtle'}>{b.status}</Badge>
                  </div>
                ))}
                {bookings.length === 0 && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>No bookings yet.</p>}
              </div>

              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 }}>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 16px' }}>Instructors to verify</h3>
                {/* Verified, not listed, is what this list tracks (#306): an instructor who listed
                    themselves is active but still unverified, and needs admin as much as anyone. */}
                {instructors.filter(i => !i.verified).slice(0, 5).map(ins => (
                  <div key={ins.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid ' + T.border }}>
                    {/* Three buttons do not fit beside the name on a phone; wrapping puts them on
                        their own line instead of pushing the card past the screen edge. */}
                    <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{ins.displayName}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: 0, overflowWrap: 'anywhere' }}>{ins.email} · {ins.language} · <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{ins.status}</span></p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <Btn size="sm" variant="ghost" onClick={() => setPreviewInstructorId(ins.id)}>Preview</Btn>
                      <Btn size="sm" variant="terracotta" onClick={() => handleVerify(ins.id)} disabled={actionLoading[ins.id]}>
                        {actionLoading[ins.id] ? '…' : 'Verify'}
                      </Btn>
                      <Btn size="sm" variant="subtle" onClick={() => handleReject(ins.id)} disabled={actionLoading[ins.id]}>
                        {actionLoading[ins.id] ? '…' : 'Reject'}
                      </Btn>
                    </div>
                  </div>
                ))}
                {instructors.filter(i => !i.verified).length === 0 && (
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>All instructors are verified.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Users ── */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.lead, color: T.espresso, margin: 0 }}>All Users</h2>
              <Btn size="sm" onClick={() => setCreateAdminOpen(true)}>Create Admin</Btn>
            </div>

            {createAdminOpen && (
              <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24, marginBottom: 20 }}>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 16px' }}>Create Admin User</h3>
                <form onSubmit={handleCreateAdmin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input value={createAdminForm.name} onChange={e => setCreateAdminForm(p => ({ ...p, name: e.target.value }))} placeholder="Name" required style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' }} />
                  <input type="email" value={createAdminForm.email} onChange={e => setCreateAdminForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" required style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' }} />
                  <input type="password" value={createAdminForm.password} onChange={e => setCreateAdminForm(p => ({ ...p, password: e.target.value }))} placeholder="Password (min 8 chars)" required minLength={8} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' }} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Btn type="submit" disabled={createAdminLoading}>{createAdminLoading ? 'Creating…' : 'Create Admin'}</Btn>
                    <Btn type="button" size="sm" variant="ghost" onClick={() => setCreateAdminOpen(false)}>Cancel</Btn>
                  </div>
                </form>
              </div>
            )}

            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: T.ivory }}>
                      {['Name', 'Email', 'Role', 'Status', 'Verified', 'Joined', 'Actions'].map(h => (
                        <th key={h} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} style={{ borderBottom: '1px solid ' + T.border }}
                        onMouseEnter={e => e.currentTarget.style.background = T.ivory}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso }}>{u.name}</td>
                        <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>{u.email}</td>
                        <td style={{ padding: '12px 16px' }}><Badge variant={u.role === 'admin' ? 'terracotta' : u.role === 'instructor' ? 'sage' : 'blue'}>{u.role}</Badge></td>
                        <td style={{ padding: '12px 16px' }}><Badge variant={u.status === 'active' ? 'sage' : 'subtle'}>{u.status}</Badge></td>
                        <td style={{ padding: '12px 16px' }}><Badge variant={u.emailVerified ? 'sage' : 'gold'}>{u.emailVerified ? 'Yes' : 'No'}</Badge></td>
                        <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood }}>{formatInTz(u.createdAt, ADMIN_TZ, DATE_ONLY)}</td>
                        <td style={{ padding: '12px 16px', display: 'flex', gap: 6 }}>
                          {u.status === 'active' && u.role !== 'admin' && (
                            <Btn size="sm" variant="ghost" onClick={() => handleSuspend(u.id)} disabled={actionLoading[`user-${u.id}`]}>
                              {actionLoading[`user-${u.id}`] ? '…' : 'Suspend'}
                            </Btn>
                          )}
                          <Btn size="sm" variant="ghost" onClick={() => { setResetModalUser(u); setResetResult(null); }}>
                            Reset PW
                          </Btn>
                          {u.role !== 'admin' && (
                            <Btn size="sm" variant="ghost" onClick={() => { setDeleteModalUser(u); setDeletePassword(''); }}>
                              <span style={{ color: T.errorRed }}>Delete</span>
                            </Btn>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No users found.</p>}
            </div>

            {/* Reset Password Modal */}
            {resetModalUser && (
              <Modal onClose={resetLoading ? undefined : () => setResetModalUser(null)} maxWidth={420} labelledBy={RESET_TITLE_ID} closeOnBackdrop={false}>
                <div style={{ padding: '32px 28px' }}>
                  <h3 id={RESET_TITLE_ID} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, color: T.espresso, margin: '0 40px 8px 0' }}>Reset Password</h3>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, marginBottom: 20 }}>
                    Generate a temporary password for <strong>{resetModalUser.name}</strong> ({resetModalUser.email}). This action will be logged.
                  </p>

                  {!resetResult && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <Btn onClick={() => handleResetPassword(resetModalUser.id)} disabled={resetLoading}>
                        {resetLoading ? 'Generating…' : 'Generate Temp Password'}
                      </Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setResetModalUser(null)}>Cancel</Btn>
                    </div>
                  )}

                  {resetResult && (
                    <div>
                      <div style={{ background: T.ivory, borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
                        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 4px' }}>Temporary password (copy now — shown once):</p>
                        <code style={{ fontFamily: 'monospace', fontSize: type.lead, color: T.espresso, wordBreak: 'break-all' }}>{resetResult.tempPassword}</code>
                      </div>
                      <Btn size="sm" onClick={() => setResetModalUser(null)}>Close</Btn>
                    </div>
                  )}
                </div>
              </Modal>
            )}

            {/* Delete User Modal — a stray click outside must not throw away the typed password. */}
            {deleteModalUser && (
              <Modal onClose={deleteLoading ? undefined : () => setDeleteModalUser(null)} maxWidth={440} labelledBy={DELETE_TITLE_ID} closeOnBackdrop={false}>
                <form onSubmit={(e) => { e.preventDefault(); handleDeleteUser(); }} style={{ padding: '32px 28px' }}>
                  <h3 id={DELETE_TITLE_ID} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, color: T.errorRed, margin: '0 40px 8px 0' }}>Permanently Delete User</h3>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, marginBottom: 8 }}>
                    This will permanently delete <strong>{deleteModalUser.name}</strong> ({deleteModalUser.email}) and all of their data — bookings, sessions, reviews, and files. <strong>This cannot be undone.</strong>
                  </p>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, marginBottom: 20 }}>
                    Enter your admin password to confirm.
                  </p>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    placeholder="Your admin password"
                    autoFocus
                    required
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 20 }}
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Btn type="submit" disabled={deleteLoading || !deletePassword}>
                      {deleteLoading ? 'Deleting…' : 'Delete Permanently'}
                    </Btn>
                    <Btn type="button" size="sm" variant="ghost" onClick={() => setDeleteModalUser(null)} disabled={deleteLoading}>Cancel</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* ── Instructors ── */}
        {tab === 'instructors' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.lead, color: T.espresso, margin: 0 }}>All Instructors</h2>
              <Btn size="sm" onClick={openCreateInstructor}>Create Instructor</Btn>
            </div>

            {createInstructorOpen && createInstructorCard}

            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                <thead>
                  <tr style={{ background: T.ivory }}>
                    {['Name', 'Email', 'Language', 'Status', 'Verified', 'Joined', 'Actions'].map(h => (
                      <th key={h} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {instructors.map(ins => (
                    <tr key={ins.id} style={{ borderBottom: '1px solid ' + T.border }}
                      onMouseEnter={e => e.currentTarget.style.background = T.ivory}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso }}>{ins.displayName}</td>
                      <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>{ins.email}</td>
                      <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>{ins.language === 'JP' ? 'Japanese' : ins.language === 'ZH' ? 'Chinese' : ins.language}</td>
                      <td style={{ padding: '12px 16px' }}><Badge variant={ins.status === 'active' ? 'sage' : ins.status === 'pending' ? 'gold' : 'subtle'}>{ins.status}</Badge></td>
                      <td style={{ padding: '12px 16px' }}><Badge variant={ins.verified ? 'sage' : 'gold'}>{ins.verified ? 'Yes' : 'No'}</Badge></td>
                      <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood }}>{formatInTz(ins.createdAt, ADMIN_TZ, DATE_ONLY)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Btn size="sm" variant="ghost" onClick={() => setPreviewInstructorId(ins.id)}>Preview</Btn>
                          {/* Verify follows `verified`, not the listing status (#306): a listed
                              instructor can still be unverified, and used to get no button. */}
                          {!ins.verified && (
                            <Btn size="sm" variant="terracotta" onClick={() => handleVerify(ins.id)} disabled={actionLoading[ins.id]}>
                              {actionLoading[ins.id] ? '…' : 'Verify'}
                            </Btn>
                          )}
                          {ins.status === 'active' && (
                            <Btn size="sm" variant="subtle" onClick={() => handleReject(ins.id)} disabled={actionLoading[ins.id]}>
                              {actionLoading[ins.id] ? '…' : 'Deactivate'}
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {instructors.length === 0 && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No instructors found.</p>}
            </div>
          </div>
        )}

        {/* Opened from Overview and Instructors alike, so it lives outside both tabs. */}
        {previewInstructorId && (
          <InstructorPreviewModal
            instructorId={previewInstructorId}
            onClose={() => setPreviewInstructorId(null)}
            onVerify={handleVerify}
            verifying={!!actionLoading[previewInstructorId]}
          />
        )}

        {/* ── Applications (AD-4, AD-5) ── */}
        {/* Invite opens the create-instructor form above the list; the list reloads once it saves. */}
        {tab === 'applications' && (
          <div>
            {createInstructorOpen && invitingApplication && createInstructorCard}
            <AdminApplicationsPanel key={applicationsKey} onInvite={handleInviteApplication} />
          </div>
        )}

        {/* ── Bookings ── */}
        {/* Filters and pages run on the server (AD-2); the panel fetches its own pages. */}
        {tab === 'bookings' && <AdminBookingsPanel instructors={instructors} />}

        {/* ── Booking payments (JTP-24) ── */}
        {/* Every booking payment and its refund flag; the panel fetches its own pages and detail. */}
        {tab === 'booking-payments' && <AdminBookingPaymentsPanel />}

        {/* ── Reported conversations (MS-4) ── */}
        {/* Admin reads a conversation only while its report is open; each read is in the activity log. */}
        {tab === 'reported' && <ReportedConversationsPanel />}

        {/* ── Blog Posts ── */}
        {tab === 'blog' && (
          <div>
            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24, marginBottom: 24 }}>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 16px' }}>Create new post</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input value={blogForm.title} onChange={e => setBlogForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' }} />
                <input value={blogForm.excerpt} onChange={e => setBlogForm(p => ({ ...p, excerpt: e.target.value }))} placeholder="Excerpt" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' }} />
                <textarea value={blogForm.content} onChange={e => setBlogForm(p => ({ ...p, content: e.target.value }))} placeholder="Content (HTML or plain text)" rows={4} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <Btn onClick={async () => {
                    setBlogSaving(true);
                    try {
                      await api.post('/blog-posts', blogForm);
                      setBlogForm({ title: '', excerpt: '', content: '', category: 'Learning' });
                      await fetchAll();
                      showSnackbar('Blog post published successfully.', { variant: 'success' });
                    } catch (err) {
                      showSnackbar('Failed to create post: ' + (err.data?.message || err.message), { variant: 'error' });
                    } finally {
                      setBlogSaving(false);
                    }
                  }} disabled={blogSaving}>{blogSaving ? 'Saving…' : 'Publish post'}</Btn>
                </div>
              </div>
            </div>
            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                  <thead>
                    <tr style={{ background: T.ivory }}>
                      {['Title', 'Category', 'Published', 'Actions'].map(h => (
                        <th key={h} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {blogPosts.map(post => (
                      <tr key={post.id} style={{ borderBottom: '1px solid ' + T.border }}
                        onMouseEnter={e => e.currentTarget.style.background = T.ivory}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso }}>{post.title}</td>
                        <td style={{ padding: '12px 16px' }}><Badge variant="sage">{post.category}</Badge></td>
                        <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood }}>{post.publishedAt ? formatInTz(post.publishedAt, ADMIN_TZ, DATE_ONLY) : 'Draft'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <Btn size="sm" variant="ghost" onClick={async () => {
                            if (!confirm('Delete this post?')) return;
                            try { await api.delete(`/blog-posts/${post.id}`); await fetchAll(); showSnackbar('Blog post deleted.', { variant: 'success' }); } catch (err) { showSnackbar('Failed to delete post.', { variant: 'error' }); }
                          }}>Delete</Btn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {blogPosts.length === 0 && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No blog posts yet.</p>}
            </div>
          </div>
        )}

        {/* ── Activity Log ── */}
        {tab === 'activity' && (
          <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                <thead>
                  <tr style={{ background: T.ivory }}>
                    {['Admin', 'Action', 'Target', 'Time'].map(h => (
                      <th key={h} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {adminActions.map(action => (
                    <tr key={action.id} style={{ borderBottom: '1px solid ' + T.border }}
                      onMouseEnter={e => e.currentTarget.style.background = T.ivory}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso }}>{action.admin?.name || 'Admin'}</td>
                      <td style={{ padding: '12px 16px' }}><Badge variant="terracotta">{action.action}</Badge></td>
                      <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut }}>{action.targetType} · {action.targetId.slice(0, 8)}</td>
                      <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood }}>{formatInTz(action.createdAt, ADMIN_TZ)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {adminActions.length === 0 && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No admin actions yet.</p>}
          </div>
        )}

        {/* ── Error Log (TTT-60) ── */}
        {tab === 'errors' && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                aria-label="Filter by error type"
                value={errorFilters.type}
                onChange={e => setErrorFilters(prev => ({ ...prev, type: e.target.value }))}
                style={{ ...inputStyle, background: T.white }}>
                <option value="">All types</option>
                {errorTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                type="date"
                aria-label="Show errors since"
                value={errorFilters.since}
                onChange={e => setErrorFilters(prev => ({ ...prev, since: e.target.value }))}
                style={{ ...inputStyle, background: T.white }} />
              {(errorFilters.type || errorFilters.since) && (
                <Btn size="sm" variant="ghost" onClick={() => setErrorFilters({ type: '', since: '' })}>Clear filters</Btn>
              )}
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: 0, marginLeft: 'auto' }}>
                {appErrors.length} shown · kept 30 days
              </p>
            </div>

            <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: T.ivory }}>
                      {['Time', 'Type', 'Message', 'Correlation'].map(h => (
                        <th key={h} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {appErrors.map(e => (
                      <tr key={e.id} style={{ borderBottom: '1px solid ' + T.border }}
                        onMouseEnter={ev => ev.currentTarget.style.background = T.ivory}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, whiteSpace: 'nowrap' }}>{formatInTz(e.createdAt, ADMIN_TZ)}</td>
                        <td style={{ padding: '12px 16px' }}><Badge variant="subtle">{e.type}</Badge></td>
                        <td style={{ padding: '12px 16px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, maxWidth: 420, wordBreak: 'break-word' }}>{e.message}</td>
                        {/* The correlation id is what ties a row to the full stack trace in the container logs. */}
                        <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: type.caption, color: T.walnut }}>{e.correlationId || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {appErrors.length === 0 && <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center', padding: '40px 0' }}>No errors logged{(errorFilters.type || errorFilters.since) ? ' for this filter' : ''}. That is the good outcome.</p>}
            </div>
          </div>
        )}

        {/* ── Server Health (TTT-68) ── */}
        {tab === 'system' && (
          <div>
            {healthError && (
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: '0 0 16px' }}>{healthError}</p>
            )}
            {!health && !healthError && (
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Reading server health…</p>
            )}
            {health && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 16, marginBottom: 24 }}>
                  {[
                    { label: 'Memory used', value: health.memory.usedPercent + '%', sub: formatBytes(health.memory.usedBytes) + ' of ' + formatBytes(health.memory.totalBytes), pct: health.memory.usedPercent },
                    { label: 'CPU load', value: health.cpu.loadPercent + '%', sub: health.cpu.count + ' cores · ' + health.cpu.load1.toFixed(2) + ' load', pct: health.cpu.loadPercent },
                    { label: 'Server uptime', value: formatUptime(health.uptimeSeconds), sub: health.platform },
                    { label: 'API process', value: formatBytes(health.api.rssBytes), sub: 'up ' + formatUptime(health.api.uptimeSeconds) + ' · ' + health.api.nodeVersion },
                  ].map(s => (
                    <div key={s.label} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '20px 18px' }}>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' }}>{s.label}</p>
                      <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: 0 }}>{s.value}</p>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.walnut, margin: '4px 0 0' }}>{s.sub}</p>
                      {typeof s.pct === 'number' && <UsageBar pct={s.pct} />}
                    </div>
                  ))}
                </div>

                <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 }}>
                  <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: T.espresso, margin: '0 0 4px' }}>Disk</h3>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: '0 0 16px' }}>
                    Filesystems on the host machine. A path the API container cannot read is shown as unavailable rather than hidden.
                  </p>
                  {health.disks.map(d => (
                    <div key={d.path} style={{ padding: '12px 0', borderBottom: '1px solid ' + T.border }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                        <p style={{ fontFamily: 'monospace', fontSize: type.body, color: T.espresso, margin: 0 }}>{d.path}</p>
                        {d.error
                          ? <Badge variant="subtle">unavailable ({d.error})</Badge>
                          : <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: 0 }}>
                              {formatBytes(d.usedBytes)} of {formatBytes(d.totalBytes)} · {formatBytes(d.freeBytes)} free
                            </p>}
                      </div>
                      {!d.error && <UsageBar pct={d.usedPercent} showLabel />}
                    </div>
                  ))}
                </div>

                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '16px 0 0' }}>
                  Live snapshot, refreshed every 10 seconds while this tab is open. No history is stored.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { AdminDashboard };
